/*
 * YOONITE application server
 *
 * hierarchyBuilder.js
 * Last update: 20.03.2020 by thomas
 *
 * (c) 2019 FSZ GmbH
 * All rights reserved.
 */

'use strict';

import _ from 'lodash';
import Reference from "~/shared/src/ci/Reference";
import Item, { ItemAttributes } from "~/shared/src/ci/Item";
import metaModel from "~/shared/src/utilities/metaModel";

type RecursionReferenceConfig = {
    computedIds: {
        inItemTypeIds: number[];
        outItemTypeIds: number[];
        referenceSetIds: number[];
        referenceTypeIds: number[];
    };
    direction: 'src' | 'dest';
    filterAttributes: any;
    filterProperties: any;
    includeProperties: number[];
    name: string;
    recursion: {
        inItemTypeIds: number[];
        outItemTypeIds: number[];
        referenceSetIds: number[];
        found: boolean;
    }[];
    recursionLevelMax: number;
    recursionLevelMin: number;
    referenceSetName: string;
    type: string[];
};

export type RecursionConfig = {
    item: {
        computedIds: {
            itemTypeIds: number[];
        };
        filterAttributes: any;
        filterProperties: any;
        includeProperties: number[];
        name: string;
        type: string;
    };
    reference?: RecursionReferenceConfig
};

export type CypherConfig = {
   errors: string[];
   fullTableJoinConditions: string;
   hints: string[];
   optional: boolean;
   query: {
       identifierToTableNameAndFieldOrAliasMapping: {
           [ id: string ]: {
               fieldName: string;
               innerTableName: string;
               tableName: string;
               type: 'item';
           }
       }
       sql: string;
       tableName: string;
   };
   recursionConfigs: RecursionConfig[];
   type: 'match';
   whereConfig: any;
}[];

type ReferenceMatch = { reference: Reference, level: number, itemAttribute: 'srcItem'|'destItem' };
type OrderConfig = [ 'byProperty'|'byAttribute', string ];

export type HierarchyNode = any;

/**
 *  @class HierarchyBuilder
 *  This helper class builds a hierarchic structure based on item, references
 *  and the description of the recursive item load in backend
 */
class HierarchyBuilder {

    orderFunctions = {
        byProperty: ( propertyName: string ) => ( ref1: ReferenceMatch, ref2: ReferenceMatch ) => {
            let retVal = 0;
            const item1 = ref1.reference[ ref1.itemAttribute ],
                  item2 = ref2.reference[ ref2.itemAttribute ],
                  propertyTypeId = metaModel[ item1?.schema ?? 'DATA' ]?.getPropertyType( propertyName )?.id,
                  val1 = propertyTypeId ? item1?.properties.get( propertyTypeId )?.value ?? 0 : 0,
                  val2 = propertyTypeId ? item2?.properties.get( propertyTypeId )?.value ?? 0 : 0;
            if ( val1 !== val2 ) {
                retVal = _.isFinite( val1 ) && _.isFinite( val2 ) ? ( val1 as number ) - ( val2 as number ) : ( val1 < val2 ? -1 : 1 );
            }
            return retVal;
        },
        byAttribute: ( attributeName: string ) => ( ref1: ReferenceMatch, ref2: ReferenceMatch ) => {
            let retVal = 0;
            const item1 = ref1.reference[ ref1.itemAttribute ],
                  item2 = ref2.reference[ ref2.itemAttribute ],
                  val1 = item1?.[ attributeName as keyof ItemAttributes ] ?? 0,
                  val2 = item2?.[ attributeName as keyof ItemAttributes ] ?? 0;
            if ( val1 !== val2 ) {
                retVal = _.isFinite( val1 ) && _.isFinite( val2 ) ? ( val1 as number ) - ( val2 as number ) : ( val1 < val2 ? -1 : 1 );
            }
            return retVal;
        }
    }

    schema: Schema;
    dataContext: string;
    orderBy: OrderConfig | false;

    rootItem: Item;
    recursionConfig: RecursionConfig[][];

    skipRootNode: boolean;

    /**
     * entryLevel
     * @type {Number}
     */
    entryLevel: number;
    maxLevel: number | false;
    excludeRefs: number[];

    rootNode: HierarchyNode;
    firstItemNode?: HierarchyNode;

    addNodeCallback: Function;

    itemsOnLevel: { [ level: number]: { [ id: string ]: true } };

    /**
     * Class constructor
     * @param dataContext
     * @param rootItem
     * @param cypherConfig
     * @param rootNode
     * @param addNodeCallback
     * @param entryLevel
     * @param maxLevel
     * @param excludeRefs
     * @param order
     * @param skipRootNode
     */
    constructor( dataContext: string, schema: Schema, rootItem: Item, cypherConfig: CypherConfig, rootNode: HierarchyNode, addNodeCallback: Function,
                 entryLevel: number = 0, maxLevel: number|false, excludeRefs: number[] = [], order: OrderConfig | false = false, skipRootNode: boolean = false ) {
        this.dataContext = dataContext;
        this.schema = schema;
        this.rootItem = rootItem;
        this.skipRootNode = skipRootNode ?? false;
        this.recursionConfig = this.flattenRecursionConfig( cypherConfig );
        this.rootNode = rootNode;
        this.addNodeCallback = addNodeCallback || ( () => {} );
        this.entryLevel = entryLevel || 0;
        this.maxLevel = maxLevel || false;
        this.excludeRefs = excludeRefs || [];
        this.orderBy = order && order.length === 2 ? order : false;
        this.itemsOnLevel = {};
    }

    /**
     * Applies a structure of CiItems to the tree by using the cypher expression.
     */
    go() {
        if ( this.recursionConfig.length ) {
            let level = this.entryLevel;
            let childrenFound = false;

            this.firstItemNode = this.addOrUpdateCiItem( this.rootItem, this.rootNode );

            do {
                childrenFound = this.addCiItemsRecursive( this.rootItem, this.rootNode, undefined, undefined, level );
                level++;
            } while ( !childrenFound && level < ( this.maxLevel || this.recursionConfig.length ) )
        }
        else if ( !this.rootNode ) {
            this.addOrUpdateCiItem( this.rootItem );
        }
    }

    /**
     * Adds a single Item in a new TreeItem to the tree
     *
     * @param {Item} ciItem the item object
     * @param {Object} [parentNode=this.rootNode] the parent node
     * @param {number} [referenceId] the CIR_ID of the reference that is related to the item
     * @param {number} [referenceSetId] the CIRS_ID of the reference that is related to the item
     * @return {Object}
     */
    addOrUpdateCiItem( ciItem: Item, parentNode?: HierarchyNode, referenceId?: number, referenceSetId?: number ): HierarchyNode {
        parentNode = parentNode ?? this.rootNode;
        // obey exclude list!
        if ( !referenceId || !this.excludeRefs.length || !this.excludeRefs.includes( referenceId ) ) {
            return this.addNodeCallback( ciItem, parentNode, referenceId, referenceSetId );
        }
        else {
            return parentNode;
        }
    }


    /**
     * Adds items via references recursively
     *
     * @param {Item} ciItem the item to be added with its children
     * @param {HierarchyNode} [parentNode] the parent tree node
     * @param {number} [refId] CIR_ID of the current reference
     * @param {number} [referenceSetId] CIRS_ID of the current reference
     * @param {number} [level] the recursion level
     * @return {boolean}
     */
    addCiItemsRecursive( ciItem: Item, parentNode: HierarchyNode, refId?: number, referenceSetId?: number, level: number = 0 ) {
        let retVal = false;
        this.itemsOnLevel[ level ] = this.itemsOnLevel[ level ] || {};
        const cypherInfo = this.recursionConfig ? this.recursionConfig[ level ] : false;
        // TODO: find solution for handling subnodes of existing item/node (they won't be caught right now)
        if ( ciItem /*&& !this.itemsOnLevel[ level ][ ciItem.id + '-' + referenceSetId ]*/ ) {
            this.itemsOnLevel[ level ][ ciItem.id + '-' + referenceSetId ] = true;
            // TODO: check if firstNodeCheck is needed anywhere (tk)
            //const itemNode = this.addOrUpdateCiItem( ciItem, parentNode, refId, referenceSetId );
            /* parentNode === this.rootNode
                ? this.firstItemNode
                : this.addOrUpdateCiItem( ciItem, parentNode, refId, referenceSetId );*/
            const itemNode: TreeNodeData|TreeNodeData[] =  ( parentNode === this.rootNode && !this.skipRootNode )
                ? this.firstItemNode
                : this.addOrUpdateCiItem( ciItem, parentNode, refId, referenceSetId );

            // use info from {@link cypherParser#getRecursionConfigFromCypher} to identify referenced items
            // to be added to tree
            if ( itemNode && cypherInfo ) {
                const relevantReferences = this.getMatchingReferences( ciItem, level, false );
                if ( relevantReferences ) {
                    retVal = true;
                    for ( const ref of relevantReferences ) {
                        const otherItem = ref.reference.destItemId === ciItem.id ? ref.reference.srcItem : ref.reference.destItem;
                        if ( otherItem ) {
                            if ( !this.skipRootNode && _.isArray( itemNode ) && itemNode.length ) {
                                for ( const itmNd of itemNode ) {
                                    this.addCiItemsRecursive( otherItem, itmNd, ref.reference.id,
                                        ref.reference.referenceSetId, level + 1 );
                                }
                            }
                            else {
                                this.addCiItemsRecursive( otherItem, itemNode, ref.reference.id,
                                    ref.reference.referenceSetId, level + 1 );
                            }
                        }
                    }
                }

            }
        }
        return retVal;
    }

    /**
     * Check for matching references in tree recursively by checking the appropriate cypher part
     * @param ciItem
     * @param level
     * @param deep
     * @return {boolean|Object[]}
     */
    getMatchingReferences( ciItem: Item, level: number = 0, deep:boolean = true ): ReferenceMatch[]|false {
        // ensure array
        const parts = deep ? this.recursionConfig.slice( level ) : ( [] as RecursionConfig[] ).concat( this.recursionConfig[ level ] );
        let retVal: ReferenceMatch[]|false = false;
        if ( ciItem.references.length ) {
            for ( const part of parts ) {
                if ( !Array.isArray( part ) && part.item && part.reference && part.reference.computedIds
                    && part.reference.computedIds.referenceSetIds ) {
                    const itemToCheck = part.reference.direction === 'src' ? 'srcItem' : 'destItem';
                    const itemToCompare = part.reference.direction === 'dest' ? 'srcItem' : 'destItem';
                    // TODO: filter by properties and reference properties
                    const matchingReferences = ciItem.references.map( refId => global.data[ this.dataContext ].references[ refId ] ).filter(
                        ref => part.reference!.computedIds.referenceSetIds.includes( ref.referenceSetId )
                            && ciItem.id === ref[ itemToCheck ]?.id
                    );
                    if ( matchingReferences.length ) {
                        retVal = retVal || [];
                        retVal = retVal.concat( matchingReferences.map(
                            ref => ( { reference: ref, level, itemAttribute: itemToCompare } ) )
                        );
                    }
                }
                level++;
            }
        }
        if ( retVal && this.orderBy && this.orderFunctions[ this.orderBy[ 0 ] ] ) {
            ( retVal as ReferenceMatch[] ).sort( this.orderFunctions[ this.orderBy[ 0 ] ]( this.orderBy[ 1 ] ) );
        }
        return retVal;
    }

    /**
     * flatten cypher recursion config to a consolidated array
     * @param cypherConfigs
     * @return {boolean|*[]}
     */
    flattenRecursionConfig( cypherConfigs: CypherConfig ): RecursionConfig[][] {
        const cypherParts: RecursionConfig[][] = [];
        /*
            maps cypher item labels to the recursion level where it occurs first
            e.g.
            MATCH (app:Application {itemKey: "FSZ:Yoonite:AppDesigner"})<-[:isMainMenuFor]-(menu:Menu)  0 - 1
            OPTIONAL MATCH (menu)<-[:isChildOf *1..5]-(menuItem:MenuItem {all:true})                    1 - 5
            OPTIONAL MATCH (menuItem)<-[:isCalledBy]-(pc:PanelConfig {all:true})                        5 - 6
            OPTIONAL MATCH (pc)<-[:hasAccess]-(g:Group)                                                 6 - 7
            OPTIONAL MATCH (menuItem)<-[:isIconFor]-(i:Image)                                           5 - 6
            OPTIONAL MATCH (menuItem)<-[:isLabelFor]-(m:Message {all:true})                             5 - 6

            => { app: 0, g: 7, i: 7, m: 7, menu: 1, menuItem: 2, pc: 6 }
         */

        const entryLevelMap: { [ id: string ]: number } = {};
        if ( cypherConfigs?.length && cypherConfigs[ 0 ] && cypherConfigs[ 0 ].recursionConfigs ) {
            let entryLevel: number = 0;
            for ( const cypherConfig of cypherConfigs ) {
                // first entry of a cypher config defines the "start point" of a recursion
                const firstConfig = cypherConfig.recursionConfigs ? cypherConfig.recursionConfigs[ 0 ] : null;
                if ( firstConfig ) {
                    if ( firstConfig.item?.name ) {
                        if ( entryLevelMap[ firstConfig.item.name ] === undefined ) {
                            entryLevelMap[ firstConfig.item.name ] = cypherParts.length;
                            entryLevel = cypherParts.length;
                        }
                        else {
                            entryLevel = entryLevelMap[ firstConfig.item.name ];
                        }
                    }

                    const whereConfig = cypherConfig.whereConfig;

                    let excludedItemTypeId = 0;
                    if ( whereConfig.op === 'not-equal' && whereConfig.arg1?.field === 'itemTypeId' && whereConfig.arg2?.value ) {
                        excludedItemTypeId = whereConfig.arg2?.value;
                    }

                    for ( const cfg of cypherConfig.recursionConfigs ) {
                        if ( entryLevelMap[ cfg.item.name ] === undefined ) {
                            entryLevelMap[ cfg.item.name ] = ++entryLevel;
                        }
                        if ( !cfg.reference?.recursion ) {
                            if ( entryLevel < cypherParts.length ) {
                                for ( let i = 0; i < cypherParts.length - entryLevel; i++ ) {
                                    cypherParts[ entryLevel + i ].push( cfg );
                                }
                            }
                            // TODO: check what this was for
                            else {
                                if ( excludedItemTypeId && cfg.reference ) {
                                    const excludedRefSetIds = metaModel[ this.schema ].getReferenceSetsForItemTypesAndReferenceType(
                                        cfg.reference.direction === 'dest' ? '*' : excludedItemTypeId,
                                        cfg.reference.direction === 'dest' ? excludedItemTypeId : '*',
                                        '*', cfg.reference.direction
                                    ).map( rset => rset.id );
                                    cfg.item.computedIds.itemTypeIds = cfg.item.computedIds.itemTypeIds.filter( id => id !== excludedItemTypeId );
                                    if ( cfg.reference ) {
                                        const itemIdAttribute = cfg.reference?.direction === 'dest' ? 'outItemTypeIds' : 'inItemTypeIds';
                                        cfg.reference.computedIds[ itemIdAttribute ] = cfg.reference.computedIds[ itemIdAttribute ].filter( id => id !== excludedItemTypeId );
                                        cfg.reference.computedIds.referenceSetIds = cfg.reference.computedIds.referenceSetIds.filter( id => !excludedRefSetIds.includes( id ) );
                                    }
                                }
                                cypherParts.push( [ cfg ] );
                            }
                        }
                        else {
                            for ( const recCfg of cfg.reference.recursion ) {
                                const clonedCfg = { ...cfg };
                                const part: RecursionConfig = clonedCfg;
                                part.reference = { ...part.reference, ...recCfg } as RecursionReferenceConfig;
                                // eslint-disable-next-line no-unused-expressions
                                ( ( entryLevel < cypherParts.length ) && cypherParts[ entryLevel ].push( part ) )
                                    || cypherParts.push( [ part ] );
                                entryLevel++;
                            }
                        }
                    }
                }
            }
        }
        return cypherParts;
    }

}

/**
 * Convenience function for easy usage of HierarchyBuilder
 * @param {string} dataContext
 * @param {Schema} schema
 * @param {Item} rootItem the root CiItem
 * @param {Object} recursionConfig the recursion config as delivered by backend
 * @param {Object} rootNode the target root node (e.g. menu item or HierarchyNode)
 * @param {Function} callback
 * @param {Number} entryLevel recursion level to start with
 * @param {Number} maxLevel maximum recursion level
 * @param {Number[]} excludeRefIds
 * @param {Array} order defines order method as array like [ 'byProperty', 'Order' ] or [ 'byAttribute', 'key' ]
 * @param {boolean} skipRootNode
 * @return {Object} return the root node (holding the hierarchic structure now)
 */
export default function getHierarchy( dataContext: string, schema: Schema, rootItem: Item, recursionConfig: CypherConfig, rootNode: HierarchyNode,
                                      callback: Function, entryLevel: number = 0, maxLevel: number|false = false,
                                      excludeRefIds: number[] = [], order: OrderConfig|false = false, skipRootNode: boolean = false ) {

    const builder = new HierarchyBuilder(
        dataContext, schema, rootItem, recursionConfig, rootNode, callback, entryLevel, maxLevel, excludeRefIds, order, skipRootNode
    );

    builder.go();
    return rootNode;
}
